import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount, useConfig, useReadContract, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { waitForTransactionReceipt } from 'wagmi/actions';
import { formatEther } from 'viem';
import { AppSidebar } from '@/components/app-sidebar';
import {
  RiImageLine,
  RiLoader4Line,
  RiWallet3Line,
  RiCoinsLine,
  RiCheckLine,
  RiArrowDownSLine,
  RiSwapLine,
  RiArrowRightLine,
} from '@remixicon/react';
import { generateRewardKeys, getMonthName, groupRewardKeysByYear } from '@/utils/nft-rewards';
import type { RewardSummary } from '@/abis/nft-dao-utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/collapsible';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/breadcrumb';
import { Separator } from '@/components/separator';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/sidebar';
import { Button } from '@/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/card';
import { Badge } from '@/components/badge';
import {
  NFT_DAO_V2_UTILS_CONTRACT_CONFIG,
  NFT_DAO_V2_MAIN_CONTRACT_CONFIG,
} from '@/configs/nft.config';
import { NFT_DAO_V2_MAIN_ABI } from '@/abis/nft-dao-v2-main';
import { ERC721_APPROVAL_ABI } from '@/abis/erc721-approval';
import type { NftData } from '@/abis/nft-dao-utils';
import type { GlobalMigrationStats, UserMigrationStats } from '@/abis/nft-dao-v2-main';
import NftDetailSidebar from '@/components/nft-detail-sidebar';
import NftMintSidebar from '@/components/nft-mint-sidebar';
import NftRewardClaimSidebar from '@/components/nft-reward-claim-sidebar';

const ZERO = '0x0000000000000000000000000000000000000000' as const;

export default function NftV2Page() {
  const { address: userAccount } = useAccount();
  const [selectedNft, setSelectedNft] = useState<NftData | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMintSidebarOpen, setIsMintSidebarOpen] = useState(false);
  const [claimPeriodKey, setClaimPeriodKey] = useState<string | null>(null);
  const [isClaimSidebarOpen, setIsClaimSidebarOpen] = useState(false);
  const [nftImages, setNftImages] = useState<Record<string, string | null>>({});
  const [migrationTxHash, setMigrationTxHash] = useState<`0x${string}` | undefined>(undefined);
  const [migrationError, setMigrationError] = useState<string | null>(null);
  const [isApprovingCollection, setIsApprovingCollection] = useState(false);

  const wagmiConfig = useConfig();

  const mainReady =
    !!NFT_DAO_V2_MAIN_CONTRACT_CONFIG.address && NFT_DAO_V2_MAIN_CONTRACT_CONFIG.address !== ZERO;
  const utilsReady =
    !!NFT_DAO_V2_UTILS_CONTRACT_CONFIG.address && NFT_DAO_V2_UTILS_CONTRACT_CONFIG.address !== ZERO;

  const { data: legacyV2Address } = useReadContract({
    address: NFT_DAO_V2_MAIN_CONTRACT_CONFIG.address,
    abi: NFT_DAO_V2_MAIN_ABI,
    functionName: 'legacyV2',
    query: {
      enabled: mainReady,
    },
  }) as { data: `0x${string}` | undefined };

  const hasLegacyBridge =
    !!legacyV2Address && legacyV2Address.toLowerCase() !== ZERO.toLowerCase();

  const {
    data: isApprovedForMigration,
    refetch: refetchLegacyApproval,
    isLoading: isLoadingLegacyApproval,
  } = useReadContract({
    address: legacyV2Address,
    abi: ERC721_APPROVAL_ABI,
    functionName: 'isApprovedForAll',
    args:
      userAccount && legacyV2Address && mainReady && hasLegacyBridge
        ? [userAccount, NFT_DAO_V2_MAIN_CONTRACT_CONFIG.address]
        : undefined,
    query: {
      enabled: !!userAccount && !!legacyV2Address && mainReady && hasLegacyBridge,
    },
  }) as { data: boolean | undefined; refetch: () => void; isLoading: boolean };

  const { data: globalMigration, refetch: refetchGlobalMigration } = useReadContract({
    address: NFT_DAO_V2_MAIN_CONTRACT_CONFIG.address,
    abi: NFT_DAO_V2_MAIN_ABI,
    functionName: 'getGlobalMigrationStats',
    query: {
      enabled: mainReady && hasLegacyBridge,
    },
  }) as { data: GlobalMigrationStats | undefined; refetch: () => void };

  const {
    data: userMigration,
    refetch: refetchUserMigration,
    isLoading: isLoadingUserMigration,
  } = useReadContract({
    address: NFT_DAO_V2_MAIN_CONTRACT_CONFIG.address,
    abi: NFT_DAO_V2_MAIN_ABI,
    functionName: 'getUserMigrationStats',
    args: userAccount ? [userAccount] : undefined,
    query: {
      enabled: !!userAccount && mainReady && hasLegacyBridge,
    },
  }) as { data: UserMigrationStats | undefined; refetch: () => void; isLoading: boolean };

  const { data: userNfts, isLoading: isLoadingNfts, refetch: refetchNfts } = useReadContract({
    address: NFT_DAO_V2_UTILS_CONTRACT_CONFIG.address,
    abi: NFT_DAO_V2_UTILS_CONTRACT_CONFIG.abi,
    functionName: 'getUserNfts',
    args: userAccount ? [userAccount] : undefined,
    query: {
      enabled: !!userAccount && utilsReady,
    },
  }) as { data: NftData[] | undefined; isLoading: boolean; refetch: () => void };

  const { writeContractAsync, isPending: isMigrating } = useWriteContract();

  const { isLoading: isMigrationConfirming, isSuccess: migrationSuccess } =
    useWaitForTransactionReceipt({
      hash: migrationTxHash,
      query: { enabled: !!migrationTxHash },
    });

  const onMigrationSettled = useCallback(() => {
    void refetchGlobalMigration();
    void refetchUserMigration();
    void refetchNfts();
    setMigrationTxHash(undefined);
  }, [refetchGlobalMigration, refetchUserMigration, refetchNfts]);

  useEffect(() => {
    if (migrationSuccess) {
      onMigrationSettled();
    }
  }, [migrationSuccess, onMigrationSettled]);

  const oldestMintTime = useMemo(() => {
    if (!userNfts || userNfts.length === 0) return null;
    return userNfts.reduce((oldest, nft) => {
      return nft.mintTime < oldest ? nft.mintTime : oldest;
    }, userNfts[0].mintTime);
  }, [userNfts]);

  const rewardKeys = useMemo(() => {
    if (!oldestMintTime) return [];
    return generateRewardKeys(oldestMintTime);
  }, [oldestMintTime]);

  const rewardKeysByYear = useMemo(() => {
    return groupRewardKeysByYear(rewardKeys);
  }, [rewardKeys]);

  const pendingIds = userMigration?.legacyTokenIdsStillOnV2 ?? [];
  const pendingCount = userMigration?.legacyStillOnV2 ?? BigInt(0);
  const canMigrateBatch = pendingIds.length > 0 && userAccount;

  const handleMigrateAll = async () => {
    setMigrationError(null);
    if (!canMigrateBatch || !mainReady || !legacyV2Address) return;
    try {
      if (!isApprovedForMigration) {
        setIsApprovingCollection(true);
        const approveHash = await writeContractAsync({
          address: legacyV2Address,
          abi: ERC721_APPROVAL_ABI,
          functionName: 'setApprovalForAll',
          args: [NFT_DAO_V2_MAIN_CONTRACT_CONFIG.address, true],
        });
        await waitForTransactionReceipt(wagmiConfig, { hash: approveHash });
        await refetchLegacyApproval();
        setIsApprovingCollection(false);
      }

      const hash = await writeContractAsync({
        address: NFT_DAO_V2_MAIN_CONTRACT_CONFIG.address,
        abi: NFT_DAO_V2_MAIN_ABI,
        functionName: 'migrateFromV2Batch',
        args: [[...pendingIds]],
      });
      setMigrationTxHash(hash);
    } catch (e) {
      setIsApprovingCollection(false);
      const msg = e instanceof Error ? e.message : 'Migration failed';
      setMigrationError(msg);
    }
  };

  const RewardMonthRow = ({ periodKey }: { periodKey: string }) => {
    const { data: summaryData, isLoading } = useReadContract({
      address: NFT_DAO_V2_UTILS_CONTRACT_CONFIG.address,
      abi: NFT_DAO_V2_UTILS_CONTRACT_CONFIG.abi,
      functionName: 'getUserRewardSummary',
      args: userAccount ? [userAccount, periodKey] : undefined,
      query: {
        enabled: !!userAccount && utilsReady,
      },
    }) as { data: [bigint, bigint, bigint, bigint, bigint, bigint] | undefined; isLoading: boolean };

    const summary: RewardSummary | null = summaryData
      ? {
          totalReward: summaryData[0],
          totalRewardLRSN: summaryData[1],
          claimableReward: summaryData[2],
          claimableRewardLRSN: summaryData[3],
          eligibleCount: summaryData[4],
          claimedCount: summaryData[5],
        }
      : null;

    if (isLoading) {
      return (
        <div className="flex items-center justify-between py-2 px-3 border-b border-border/50 last:border-b-0">
          <span className="text-sm font-medium text-muted-foreground">{getMonthName(periodKey)}</span>
          <RiLoader4Line className="size-4 animate-spin text-[#FF8A3C]" />
        </div>
      );
    }

    if (!summary) {
      return null;
    }

    if (summary.totalReward === BigInt(0) && summary.totalRewardLRSN === BigInt(0)) {
      return null;
    }

    const isAllClaimed = summary.claimedCount === summary.eligibleCount && summary.eligibleCount > BigInt(0);
    const hasClaimable = summary.claimableReward > BigInt(0) || summary.claimableRewardLRSN > BigInt(0);

    return (
      <div className="flex items-center justify-between py-2.5 px-3 border-b border-border/50 last:border-b-0 hover:bg-background/50 transition-colors duration-200">
        <div className="flex items-center gap-2 min-w-[120px]">
          <span className="text-sm font-medium">{getMonthName(periodKey)}</span>
          {isAllClaimed && (
            <Badge variant="outline" className="h-5 px-1.5 text-xs bg-green-500/10 text-green-400 border-green-500/30">
              <RiCheckLine className="size-3 mr-1" />
              Claimed
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-4 text-xs flex-1 justify-end">
          <div className="text-right min-w-[70px]">
            <div className="text-muted-foreground">Total</div>
            <div className="font-medium text-[#FF8A3C]">
              {parseFloat(formatEther(summary.totalReward)).toFixed(4)} LRS
            </div>
            <div className="font-medium text-[#FF8A3C]">
              {parseFloat(formatEther(summary.totalRewardLRSN)).toFixed(4)} LRSN
            </div>
          </div>
          {hasClaimable && (
            <div className="text-right min-w-[70px] border-l border-border/50 pl-4">
              <div className="text-muted-foreground">Claimable</div>
              <div className="font-medium">
                {parseFloat(formatEther(summary.claimableReward)).toFixed(4)} LRS
              </div>
              <div className="font-medium">
                {parseFloat(formatEther(summary.claimableRewardLRSN)).toFixed(4)} LRSN
              </div>
            </div>
          )}
          <div className="text-right min-w-[80px] border-l border-border/50 pl-4">
            <div className="text-muted-foreground">NFTs</div>
            <div className="font-medium">{summary.eligibleCount.toString()} eligible</div>
            <div className="text-muted-foreground">{summary.claimedCount.toString()} claimed</div>
          </div>

          {hasClaimable && (
            <div className="text-right min-w-[96px] border-l border-border/50 pl-4">
              <Button
                size="sm"
                type="button"
                onClick={() => {
                  setClaimPeriodKey(periodKey);
                  setIsClaimSidebarOpen(true);
                }}
                className="cursor-pointer bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                Claim
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!userNfts || userNfts.length === 0) {
      setNftImages({});
      return;
    }

    const fetchImages = async () => {
      const imageMap: Record<string, string | null> = {};

      await Promise.all(
        userNfts.map(async (nft) => {
          const tokenId = nft.tokenId.toString();
          try {
            let url = nft.tokenURI;

            if (url.startsWith('ipfs://')) {
              url = `https://ipfs.io/ipfs/${url.replace('ipfs://', '')}`;
            }

            const isJsonUrl = url.toLowerCase().endsWith('.json') || url.toLowerCase().includes('.json');

            let metadata: unknown;

            if (isJsonUrl) {
              const response = await fetch(url);
              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }

              const contentType = response.headers.get('content-type');
              if (contentType && contentType.includes('application/json')) {
                metadata = await response.json();
              } else {
                const text = await response.text();
                try {
                  metadata = JSON.parse(text);
                } catch {
                  throw new Error('Response is not valid JSON');
                }
              }
            } else {
              const response = await fetch(url);
              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }

              const contentType = response.headers.get('content-type');
              if (contentType && contentType.includes('application/json')) {
                metadata = await response.json();
              } else {
                const text = await response.text();
                try {
                  metadata = JSON.parse(text);
                } catch {
                  throw new Error('Response is not valid JSON');
                }
              }
            }

            const meta = metadata as { image?: string };
            if (meta && meta.image) {
              let imageUrl = meta.image;
              if (imageUrl.startsWith('ipfs://')) {
                imageUrl = `https://ipfs.io/ipfs/${imageUrl.replace('ipfs://', '')}`;
              }
              imageMap[tokenId] = imageUrl;
            } else {
              imageMap[tokenId] = null;
            }
          } catch (error) {
            console.error(`Error fetching image for NFT ${tokenId}:`, error);
            imageMap[tokenId] = null;
          }
        })
      );

      setNftImages(imageMap);
    };

    fetchImages();
  }, [userNfts]);

  const handleNftClick = (nft: NftData) => {
    setSelectedNft(nft);
    setIsSidebarOpen(true);
  };

  const formatDate = (timestamp: bigint) => {
    return new Date(Number(timestamp) * 1000).toLocaleDateString();
  };

  const globalTotal = globalMigration?.legacyIdsTotal ?? BigInt(0);
  const globalMigrated = globalMigration?.migratedCount ?? BigInt(0);
  const migrationProgressPct =
    globalTotal > BigInt(0)
      ? Math.min(100, Number((globalMigrated * BigInt(10000)) / globalTotal) / 100)
      : 0;

  const isMigrationBusy = isMigrating || isMigrationConfirming || isApprovingCollection;
  const isMigrateAllDisabled = !canMigrateBatch || isMigrationBusy || isLoadingLegacyApproval;

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div className="px-4 md:px-6 lg:px-8 @container">
          <div className="w-full max-w-6xl mx-auto">
            <header className="flex flex-wrap gap-3 min-h-20 py-4 shrink-0 items-center transition-all ease-linear border-b">
              <div className="flex flex-1 items-center gap-2">
                <SidebarTrigger className="-ms-1" />
                <div className="max-lg:hidden lg:contents">
                  <Separator orientation="vertical" className="me-2 data-[orientation=vertical]:h-4" />
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem className="hidden md:block">
                        <BreadcrumbLink asChild>
                          <Link to="/">Home</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to="/nfts">NFTs</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbPage>NFT V2</BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <ConnectButton />
              </div>
            </header>

            <div className="py-6">
              <div className="flex items-center mb-6">
                <div className="w-1 h-8 bg-[#FF8A3C] mr-3" />
                <div className="flex items-center justify-between w-full gap-3 flex-wrap">
                  <h1 className="text-3xl font-bold tracking-tight">NFT V2</h1>
                  <Button
                    type="button"
                    onClick={() => setIsMintSidebarOpen(true)}
                    className="cursor-pointer bg-primary hover:bg-primary/90 text-primary-foreground font-medium transition-colors duration-200"
                  >
                    Mint / Buy NFT
                  </Button>
                </div>
              </div>

              <div className="w-full max-w-xs h-1 bg-gradient-to-r from-[#FF8A3C] to-transparent mb-8 rounded-full" />

              {/* Migrate from V1 */}
              {mainReady && (
                <Card className="mb-8 border border-[#FF8A3C]/25 bg-sidebar/80 shadow-sm shadow-[#FF8A3C]/5 transition-colors duration-200 hover:border-[#FF8A3C]/40">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xl flex flex-wrap items-center gap-2">
                      <RiSwapLine className="size-6 text-[#FF8A3C] shrink-0" aria-hidden />
                      <span>Migrate from NFT V1</span>
                      <Badge variant="outline" className="text-xs border-[#FF8A3C]/40 text-[#FF8A3C]">
                        Same token IDs on V2
                      </Badge>
                    </CardTitle>
                    <p className="text-sm text-muted-foreground leading-relaxed pt-1">
                      Move NFTs from the legacy V1 contract into this V2 collection. The flow approves the V2
                      migration contract on your V1 collection (<span className="font-medium text-foreground/80">setApprovalForAll</span> if
                      needed), then migrates: each legacy token is sent to the burn address on V1 and the same ID is
                      minted here with preserved metadata and level.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {!hasLegacyBridge ? (
                      <p className="text-sm text-muted-foreground">
                        Legacy V1 bridge is not configured for this deployment (no{' '}
                        <code className="text-xs rounded bg-muted px-1 py-0.5">legacyV2</code> address).
                      </p>
                    ) : (
                      <>
                        <div className="grid gap-3 sm:grid-cols-3">
                          <div className="rounded-lg border border-border bg-background/50 px-3 py-2.5 transition-colors duration-200">
                            <div className="text-xs text-muted-foreground">Legacy IDs (V1 range)</div>
                            <div className="text-lg font-semibold tabular-nums">{globalTotal.toString()}</div>
                          </div>
                          <div className="rounded-lg border border-border bg-background/50 px-3 py-2.5 transition-colors duration-200">
                            <div className="text-xs text-muted-foreground">Migrated to V2</div>
                            <div className="text-lg font-semibold tabular-nums text-emerald-400/90">
                              {globalMigrated.toString()}
                            </div>
                          </div>
                          <div className="rounded-lg border border-border bg-background/50 px-3 py-2.5 transition-colors duration-200">
                            <div className="text-xs text-muted-foreground">Still on V1</div>
                            <div className="text-lg font-semibold tabular-nums">
                              {(globalMigration?.pendingCount ?? BigInt(0)).toString()}
                            </div>
                          </div>
                        </div>

                        <div>
                          <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                            <span>Network migration progress</span>
                            <span className="tabular-nums">{migrationProgressPct.toFixed(1)}%</span>
                          </div>
                          <div
                            className="h-2 w-full rounded-full bg-muted overflow-hidden"
                            role="progressbar"
                            aria-valuenow={migrationProgressPct}
                            aria-valuemin={0}
                            aria-valuemax={100}
                          >
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-[#FF8A3C] to-[#FF8A3C]/70 transition-all duration-300"
                              style={{ width: `${migrationProgressPct}%` }}
                            />
                          </div>
                        </div>

                        {!userAccount ? (
                          <p className="text-sm text-muted-foreground">Connect your wallet to see pending V1 NFTs.</p>
                        ) : isLoadingUserMigration ? (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <RiLoader4Line className="size-4 animate-spin text-[#FF8A3C]" />
                            Checking migration status…
                          </div>
                        ) : (
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="space-y-2 min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-medium">Your NFTs still on V1</span>
                                {pendingCount > BigInt(0) && (
                                  <Badge className="bg-amber-500/15 text-amber-200 border-amber-500/40">
                                    {pendingCount.toString()} pending
                                  </Badge>
                                )}
                                {userMigration && userMigration.legacyMigratedOwnedOnV3 > BigInt(0) && (
                                  <Badge variant="outline" className="border-emerald-500/40 text-emerald-300">
                                    {userMigration.legacyMigratedOwnedOnV3.toString()} migrated (on V2)
                                  </Badge>
                                )}
                              </div>
                              {pendingIds.length > 0 ? (
                                <div className="flex flex-wrap gap-1.5">
                                  {pendingIds.map((id) => (
                                    <span
                                      key={id.toString()}
                                      className="inline-flex items-center rounded-md border border-border bg-background/80 px-2 py-0.5 font-mono text-xs text-foreground/90"
                                    >
                                      #{id.toString()}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-muted-foreground">
                                  No legacy NFTs left on V1 for this wallet.
                                </p>
                              )}
                            </div>
                            <div className="flex flex-col gap-2 sm:items-end shrink-0">
                              {userAccount && isApprovedForMigration === false && pendingIds.length > 0 && (
                                <p className="text-xs text-muted-foreground text-right max-w-[260px]">
                                  The button will first approve the V2 contract for all your V1 NFTs, then run
                                  migration.
                                </p>
                              )}
                              {userAccount && isApprovedForMigration === true && pendingIds.length > 0 && (
                                <p className="text-xs text-emerald-400/90 text-right max-w-[260px]">
                                  V2 contract is approved on your V1 collection.
                                </p>
                              )}
                              <Button
                                type="button"
                                disabled={isMigrateAllDisabled}
                                onClick={() => void handleMigrateAll()}
                                className="cursor-pointer bg-[#FF8A3C] hover:bg-[#FF8A3C]/90 text-background font-medium transition-colors duration-200 min-w-[200px]"
                              >
                                {isApprovingCollection ? (
                                  <>
                                    <RiLoader4Line className="size-4 animate-spin mr-2 inline" />
                                    Approving collection…
                                  </>
                                ) : isLoadingLegacyApproval ? (
                                  <>
                                    <RiLoader4Line className="size-4 animate-spin mr-2 inline" />
                                    Checking approval…
                                  </>
                                ) : isMigrationBusy ? (
                                  <>
                                    <RiLoader4Line className="size-4 animate-spin mr-2 inline" />
                                    {isMigrationConfirming ? 'Confirming migration…' : 'Migrating…'}
                                  </>
                                ) : (
                                  <>
                                    <RiSwapLine className="size-4 mr-2 inline" />
                                    Migrate all from V1
                                  </>
                                )}
                              </Button>
                              <Link
                                to="/nfts/v1"
                                className="inline-flex items-center justify-center gap-1.5 text-sm text-[#FF8A3C] hover:text-[#FF8A3C]/85 transition-colors duration-200 cursor-pointer"
                              >
                                Open NFT V1
                                <RiArrowRightLine className="size-4" aria-hidden />
                              </Link>
                            </div>
                          </div>
                        )}

                        {migrationError && (
                          <p className="text-sm text-red-400/90 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2">
                            {migrationError}
                          </p>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {!mainReady && (
                <Card className="mb-8 border border-dashed border-border bg-sidebar/50">
                  <CardContent className="p-4 text-sm text-muted-foreground">
                    Set <code className="text-xs">VITE_NFT_DAO_V2_CONTRACT_ADDRESS</code> to enable migration and V2
                    contract features.
                  </CardContent>
                </Card>
              )}

              <div className="flex flex-col lg:flex-row gap-6 mb-8">
                <div className="w-full lg:w-[40%]">
                  {userAccount && userNfts && userNfts.length > 0 && Object.keys(rewardKeysByYear).length > 0 ? (
                    <Card className="bg-sidebar border border-border h-full">
                      <CardHeader>
                        <CardTitle className="text-xl flex items-center gap-2">
                          <RiCoinsLine className="size-6 text-[#FF8A3C]" />
                          Reward Summary
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {Object.keys(rewardKeysByYear)
                            .sort((a, b) => parseInt(b, 10) - parseInt(a, 10))
                            .map((year) => {
                              const monthKeys = rewardKeysByYear[year];
                              const hasRewards = monthKeys.length > 0;

                              if (!hasRewards) return null;

                              return (
                                <Collapsible key={year} defaultOpen={year === new Date().getFullYear().toString()}>
                                  <CollapsibleTrigger className="w-full cursor-pointer">
                                    <div className="flex items-center justify-between w-full py-3 px-4 bg-background rounded-lg border border-border hover:border-[#FF8A3C]/50 transition-colors duration-200">
                                      <div className="flex items-center gap-2">
                                        <span className="text-lg font-semibold text-[#FF8A3C]">{year}</span>
                                        <span className="text-xs text-muted-foreground">
                                          ({monthKeys.length} {monthKeys.length === 1 ? 'month' : 'months'})
                                        </span>
                                      </div>
                                      <RiArrowDownSLine className="size-5 text-muted-foreground transition-transform duration-200 data-[state=open]:rotate-180" />
                                    </div>
                                  </CollapsibleTrigger>
                                  <CollapsibleContent>
                                    <div className="mt-2 bg-sidebar rounded-lg border border-border overflow-hidden">
                                      {monthKeys
                                        .reverse()
                                        .map((key) => (
                                          <RewardMonthRow key={key} periodKey={key} />
                                        ))}
                                    </div>
                                  </CollapsibleContent>
                                </Collapsible>
                              );
                            })}
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="bg-sidebar border border-border">
                      <CardContent className="p-6 text-center">
                        <RiCoinsLine className="size-12 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">No reward data available</p>
                      </CardContent>
                    </Card>
                  )}
                </div>

                <div className="w-full lg:w-[60%]">
                  {!utilsReady ? (
                    <Card className="bg-sidebar border border-border">
                      <CardContent className="p-12 text-center">
                        <p className="text-muted-foreground text-sm">
                          Set <code className="text-xs">VITE_NFT_DAO_V2_UTILS_CONTRACT_ADDRESS</code> to load NFT data.
                        </p>
                      </CardContent>
                    </Card>
                  ) : !userAccount ? (
                    <Card className="bg-sidebar border border-border">
                      <CardContent className="p-12 text-center">
                        <RiWallet3Line className="size-16 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">Please connect your wallet to view your NFTs</p>
                      </CardContent>
                    </Card>
                  ) : isLoadingNfts ? (
                    <Card className="bg-sidebar border border-border">
                      <CardContent className="p-12 text-center">
                        <RiLoader4Line className="size-12 animate-spin text-[#FF8A3C] mx-auto mb-4" />
                        <p className="text-muted-foreground">Loading your NFTs...</p>
                      </CardContent>
                    </Card>
                  ) : !userNfts || userNfts.length === 0 ? (
                    <Card className="bg-sidebar border border-border">
                      <CardContent className="p-12 text-center">
                        <RiImageLine className="size-16 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">No NFTs found in your wallet</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {userNfts.map((nft) => {
                        const tokenId = nft.tokenId.toString();
                        const imageUrl = nftImages[tokenId];
                        return (
                          <Card
                            key={tokenId}
                            className="bg-sidebar border border-border hover:border-[#FF8A3C]/50 transition-colors duration-200 cursor-pointer"
                            onClick={() => handleNftClick(nft)}
                          >
                            <CardContent className="px-4">
                              <div className="bg-muted rounded-lg mb-3 flex items-center justify-center overflow-hidden aspect-square">
                                {imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={`NFT #${tokenId}`}
                                    className="w-full h-full object-contain"
                                    onError={() => {
                                      setNftImages((prev) => ({ ...prev, [tokenId]: null }));
                                    }}
                                  />
                                ) : (
                                  <RiImageLine className="size-16 text-muted-foreground" />
                                )}
                              </div>
                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium">#{tokenId}</span>
                                  <Badge variant="outline" className="bg-[#FF8A3C]/10 text-[#FF8A3C] border-[#FF8A3C]">
                                    L{nft.level.toString()}
                                  </Badge>
                                </div>
                                <div className="text-xs text-muted-foreground">Minted: {formatDate(nft.mintTime)}</div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </SidebarInset>

      <NftDetailSidebar
        isOpen={isSidebarOpen}
        nft={selectedNft}
        utilsConfig={NFT_DAO_V2_UTILS_CONTRACT_CONFIG}
        onClose={() => {
          setIsSidebarOpen(false);
          setSelectedNft(null);
        }}
      />

      <NftMintSidebar
        isOpen={isMintSidebarOpen}
        onClose={() => setIsMintSidebarOpen(false)}
        onMinted={() => {
          void refetchNfts();
        }}
      />

      <NftRewardClaimSidebar
        isOpen={isClaimSidebarOpen}
        onClose={() => setIsClaimSidebarOpen(false)}
        periodKey={claimPeriodKey}
        utilsConfig={NFT_DAO_V2_UTILS_CONTRACT_CONFIG}
        onClaimed={() => {
          void refetchNfts();
        }}
      />
    </SidebarProvider>
  );
}
